import { DatePicker } from '../form';
import { SquareButton } from '../UI';

import { Currency } from '../../types/common';
import { useState } from 'react';
import {
  computePercentage,
  getValuePercentageFromBase,
} from '../../helpers/functions';
import { Target } from '../Widget/types';
import { IStrategyAssetTarget } from '../../types/model';
import { AssetTargetStop } from './AssetTargetStop';
import { useTranslation } from 'react-i18next';
import InfoTooltip from '../InfoTooltip/Info-tooltip';
import { CurrencySymbolEnum } from '../../constants/enums';
import { Debug } from '../Debug/Debug';
import { logger } from '../../helpers/logger';

const TARGET = ['AMOUNT', 'PERCENTAGE'] as const;

export const AssetTarget = ({
  canRemove = true,
  reportingCurrency,
  entryPrice,
  assetCurrency,
  entryExchangePrice,
  purchasedAt,
  removeClassName = '',
  target,
  targetIndex = 0,
  removeTarget = () => {},
  saveChanges,
  updateAsset = () => {},
}: {
  canRemove?: boolean;
  assetCurrency: string;
  reportingCurrency: Currency['code'];
  entryPrice: number;
  purchasedAt: string;
  removeClassName?: string;
  target: Target;
  entryExchangePrice: number;
  targetIndex?: number;
  removeTarget?(targetIndex: number): void;
  saveChanges?(): void;
  updateAsset?(targetIndex: number, target: Target): void;
}) => {
  const { endDate } = target;
  const [targetType, setTargetType] = useState<'AMOUNT' | 'PERCENTAGE'>(
    'AMOUNT'
  );
  const [lastTarget, setLastTarget] = useState<Target>(target);
  const { t } = useTranslation();

  const updateTarget = (
    key: keyof IStrategyAssetTarget,
    value?: Date | string | number
  ) => {
    logger.log('AssetTarget:updateTarget', target, key, value);

    const newUpdatedTarget: Target = { ...target };

    const updatedTarget = {
      ...target,
      [key]:
        typeof value !== 'number' && value !== undefined
          ? value
          : {
              AMOUNT:
                targetType === 'AMOUNT' || !value
                  ? value
                  : key === 'stopLoss'
                  ? entryPrice - getValuePercentageFromBase(entryPrice, value)
                  : entryPrice + getValuePercentageFromBase(entryPrice, value),
              AMOUNT_IN_REPORTING_CURRENCY:
                targetType === 'AMOUNT' || !value
                  ? value
                    ? value * entryExchangePrice
                    : value
                  : key === 'stopLoss'
                  ? (entryPrice -
                      getValuePercentageFromBase(entryPrice, value)) *
                    entryExchangePrice
                  : (entryPrice +
                      getValuePercentageFromBase(entryPrice, value)) *
                    entryExchangePrice,
              PERCENTAGE:
                targetType === 'PERCENTAGE' || !value
                  ? value
                  : Math.abs(computePercentage(entryPrice, value, 100)),
            },
    };

    setLastTarget(updatedTarget);
    updateAsset(targetIndex, updatedTarget);
  };

  const currencySign = CurrencySymbolEnum[reportingCurrency];
  return (
    <div
      className={`
        flex
        relative
        items-start
        h-auto py-6 gap-16 flex-wrap justify-between
      `}
    >
      {/*-----------------------------------------------------*/}

      {/* calendar */}
      <div className="flex w-full gap-8 flex-row mt-8">
        <div className={'w-1/2'}>
          <DatePicker
            id={'endDate'}
            placeholder={t('strategies.assetAdvancedSettings.endDate')}
            date={endDate}
            minDate={new Date(purchasedAt)}
            onChange={(date) => {
              const dt = date as Date;
              dt.setUTCDate(dt.getDate());
              dt.setUTCHours(0);
              updateTarget('endDate', dt);
            }}
            showLabel={true}
            fixedLabel={false}
            size={'full'}
            label={t('strategies.assetAdvancedSettings.endDate')}
            validate={(value) => !!value}
          />
        </div>
        <div className={'w-1/2 flex justify-end'}>
          {/* remove */}
          {canRemove && (
            <span
              className={`
            ${removeClassName}
            text-xs
            font-medium
            cursor-pointer
          `}
              onClick={() => removeTarget(targetIndex)}
            >
              {t('strategies.assetAdvancedSettings.clear')}
            </span>
          )}
        </div>
      </div>

      {/* $ % */}
      <div
        className={`
          flex
          w-full
        `}
      >
        {TARGET.map((type, index) => (
          <SquareButton
            key={type + index}
            className="mr-4"
            isFocused={type === targetType}
            onClick={() => setTargetType(type)}
          >
            {type === 'AMOUNT' ? currencySign : '%'}
          </SquareButton>
        ))}
        <InfoTooltip
          placement="right"
          content={t(
            'strategies.assetAdvancedSettings.amountPercentageTooltip'
          )}
          isTooltipIconVisible
          tooltipPopupClassname={'flex w-96'}
          tooltipIconClassName={'ml-1 text-grey-30'}
          tooltipClassname={'inline-flex items-center'}
        />
      </div>
      {/*-----------------------------------------------------*/}

      {/*-----------------------------------------------------*/}
      <div className={`flex w-full`}>
        {/* STOPS */}
        <div className={`gap-8 flex w-full flex-row justify-between`}>
          {(['Profit', 'Loss'] as const).map((title) => (
            <AssetTargetStop
              assetCurrency={assetCurrency}
              reportingCurrency={reportingCurrency}
              entryExchangePrice={entryExchangePrice}
              entryPrice={entryPrice}
              target={target}
              targetType={targetType}
              title={title}
              updateTarget={updateTarget}
              key={title}
            />
          ))}
        </div>
      </div>
      {/*-----------------------------------------------------*/}
      <Debug data={{ target, entryExchangePrice }} style={{ width: '100%' }} />

      {/*-----------------------------------------------------*/}
      <div className={'flex justify-end'}>
        {saveChanges && (
          <span
            className={`
            ${removeClassName}
            text-xs
            font-medium
            cursor-pointer
          `}
            onClick={() => 'saveTarget(targetIndex)'}
          >
            {t('strategies.assetAdvancedSettings.save')}
          </span>
        )}
      </div>
      {/*-----------------------------------------------------*/}
    </div>
  );
};
